/**
 * StorageUrlService.ts
 *
 * Service unifié pour gérer les storage paths et signed URLs
 * PHASE 2: Stocker storage_path en DB, générer signed URLs à la demande avec cache
 *
 * Note: Ce service délègue maintenant à StorageService.createSignedUrl() pour unifier le cache.
 * Les fonctions de formatage/parsing restent ici pour compatibilité.
 */

import { StorageService } from './StorageService';
import { logger } from '../utils/ProfessionalLogger';

/**
 * Préfixe pour identifier les storage paths dans les URLs
 * Format: supabase://bucket-name/storage-path
 */
const STORAGE_PATH_PREFIX = 'supabase://';

/**
 * Bucket par défaut pour les uploads
 */
const DEFAULT_BUCKET = 'template-editor-uploads';

/**
 * TTL par défaut pour les signed URLs (1 heure)
 */
const DEFAULT_SIGNED_URL_TTL = 3600;

/**
 * Convertit un storage path en format URL reconnaissable
 * @param storagePath - Chemin de stockage (ex: userId/projectId/file.png)
 * @param bucket - Nom du bucket (optionnel, défaut: template-editor-uploads)
 * @returns URL formatée (ex: supabase://template-editor-uploads/userId/projectId/file.png)
 */
export function formatStoragePathAsUrl(
  storagePath: string,
  bucket: string = DEFAULT_BUCKET
): string {
  return `${STORAGE_PATH_PREFIX}${bucket}/${storagePath}`;
}

/**
 * Extrait le storage path et le bucket d'une URL formatée
 * @param url - URL formatée (ex: supabase://template-editor-uploads/userId/projectId/file.png)
 * @returns { bucket: string, storagePath: string } | null
 */
export function parseStoragePathFromUrl(
  url: string
): { bucket: string; storagePath: string } | null {
  if (!url.startsWith(STORAGE_PATH_PREFIX)) {
    return null;
  }

  const withoutPrefix = url.slice(STORAGE_PATH_PREFIX.length);
  const parts = withoutPrefix.split('/');

  if (parts.length < 2) {
    return null;
  }

  const bucket = parts[0];
  const storagePath = parts.slice(1).join('/');

  return { bucket, storagePath };
}

/**
 * Vérifie si une URL est un storage path formaté
 */
export function isStoragePathUrl(url: string): boolean {
  return url.startsWith(STORAGE_PATH_PREFIX);
}

/**
 * Génère une signed URL à partir d'un storage path
 * Délègue à StorageService.createSignedUrl() pour unifier le cache
 *
 * @param storagePath - Chemin de stockage (ex: userId/projectId/file.png)
 * @param bucket - Nom du bucket (optionnel, défaut: template-editor-uploads)
 * @param ttl - TTL en secondes (optionnel, défaut: 1 heure)
 * @returns Promise<string> - Signed URL
 */
export async function getSignedUrlFromStoragePath(
  storagePath: string,
  bucket: string = DEFAULT_BUCKET,
  ttl: number = DEFAULT_SIGNED_URL_TTL
): Promise<string> {
  // PHASE 2: Déléguer à StorageService pour unifier le cache
  return StorageService.createSignedUrl(bucket, storagePath, ttl);
}

/**
 * Convertit une URL (storage path formaté ou signed URL) en signed URL
 * Si c'est déjà une signed URL, la retourne telle quelle
 * Si c'est un storage path formaté, génère la signed URL
 * Sinon, retourne l'URL telle quelle (URL publique ou autre)
 *
 * @param url - URL à convertir
 * @param ttl - TTL en secondes (optionnel, défaut: 1 heure)
 * @returns Promise<string> - Signed URL ou URL originale
 */
export async function ensureSignedUrl(
  url: string,
  ttl: number = DEFAULT_SIGNED_URL_TTL
): Promise<string> {
  // Si c'est déjà une signed URL ou une URL publique, la retourner telle quelle
  if (!isStoragePathUrl(url)) {
    return url;
  }

  // Extraire le storage path
  const parsed = parseStoragePathFromUrl(url);
  if (!parsed) {
    logger.warn(`Impossible de parser l'URL storage path: ${url}`);
    return url;
  }

  // Générer la signed URL
  try {
    return await getSignedUrlFromStoragePath(parsed.storagePath, parsed.bucket, ttl);
  } catch (error) {
    logger.error(`Erreur lors de la génération de la signed URL pour ${url}:`, error);
    // En cas d'erreur, retourner l'URL originale
    return url;
  }
}

/**
 * Convertit une URL storage path formatée en signed URL de manière synchrone
 * Retourne l'URL originale si ce n'est pas un storage path
 *
 * Note: Cette fonction ne génère pas de signed URL, elle retourne simplement
 * l'URL originale. Utilisez ensureSignedUrl() pour obtenir une signed URL.
 */
export function getStoragePathFromUrl(url: string): string | null {
  const parsed = parseStoragePathFromUrl(url);
  return parsed ? parsed.storagePath : null;
}
